/**
 * SignupTrendsChart
 *
 * Daily Signup Analysis chart with day-of-week breakdown.
 */
import React from 'react';
import { Info, TrendingUp, Activity, Users } from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  Tooltip as RechartsTooltip
} from 'recharts';
import AnalyticsTooltip from './AnalyticsTooltip';

const SignupTrendsChart = ({ analyticsData, getPeriodDisplayName, selectedPeriod }) => {
  const signupData = analyticsData.signupTrends || [];
  
  // Calculate peak day
  const maxDay = signupData.length > 0 
    ? signupData.reduce((max, day) => day.signups > max.signups ? day : max, signupData[0])
    : { signups: 0, date: '' };
  
  // Calculate daily average
  const total = signupData.reduce((sum, day) => sum + day.signups, 0);
  const dailyAvg = signupData.length > 0 ? total / signupData.length : 0;
  
  // Calculate day of week totals
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayTotals = [0, 0, 0, 0, 0, 0, 0];
  signupData.forEach(day => {
    const date = new Date(day.date);
    const dayOfWeek = date.getDay();
    dayTotals[dayOfWeek] += day.signups || 0;
  });
  const maxSignups = Math.max(...dayTotals);

  // Calculate best weekday
  const weekdayTotals = [0, 0, 0, 0, 0];
  const weekdayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  signupData.forEach(day => {
    const date = new Date(day.date);
    const dayOfWeek = date.getDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      weekdayTotals[dayOfWeek - 1] += day.signups || 0;
    }
  });
  const maxWeekdayIndex = weekdayTotals.indexOf(Math.max(...weekdayTotals));
  const bestWeekday = weekdayNames[maxWeekdayIndex] || 'N/A';
  const bestWeekdayCount = weekdayTotals[maxWeekdayIndex] || 0;

  // Calculate weekend total
  let weekendTotal = 0;
  signupData.forEach(day => {
    const date = new Date(day.date);
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      weekendTotal += day.signups || 0;
    }
  });
  const weekendPercentage = total > 0 ? (weekendTotal / total * 100).toFixed(1) : 0;

  // Calculate most consistent day
  const avg = dayTotals.reduce((a, b) => a + b, 0) / 7;
  const variances = dayTotals.map(total => Math.abs(total - avg));
  const minVarianceIndex = variances.indexOf(Math.min(...variances));
  const mostConsistent = dayNames[minVarianceIndex];
  const consistentCount = dayTotals[minVarianceIndex];

  return (
    <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Daily Signup Trends
        </h3>
        <AnalyticsTooltip content="Track daily user signups to identify your best acquisition days and patterns. Helps you understand when users are most likely to discover and join your platform." id="daily-signups-tooltip">
          <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
        </AnalyticsTooltip>
      </div>
      
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={signupData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis 
            dataKey="date" 
            tickFormatter={(date) => {
              const d = new Date(date);
              return d.toLocaleDateString('en-US', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric' 
              });
            }}
            stroke="#6B7280"
            fontSize={11}
            angle={-45}
            textAnchor="end"
            height={80}
            interval="preserveStartEnd"
          />
          <YAxis 
            stroke="#6B7280" 
            fontSize={12}
            label={{ value: 'Signups', angle: -90, position: 'insideLeft' }}
          />
          <RechartsTooltip 
            labelFormatter={(date) => {
              const d = new Date(date);
              return d.toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric',
                month: 'long', 
                day: 'numeric' 
              });
            }}
            formatter={(value) => [value, 'New Signups']}
            contentStyle={{
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#F9FAFB'
            }}
          />
          <Bar 
            dataKey="signups" 
            fill="#3B82F6"
            radius={[4, 4, 0, 0]}
            name="Daily Signups"
          />
        </BarChart>
      </ResponsiveContainer>
      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Peak Day */}
        <div className="bg-blue-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Peak Day</p>
              <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                {signupData.length > 0 ? `${maxDay.signups} signups` : 'No data'}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {signupData.length > 0 && new Date(maxDay.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* Average Daily */}
        <div className="bg-green-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <Activity className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-green-700 dark:text-green-300 font-medium">Daily Average</p>
              <p className="text-lg font-bold text-green-900 dark:text-green-100">
                {dailyAvg.toFixed(1)} signups
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">Per day</p>
            </div>
          </div>
        </div>

        {/* Total Period */}
        <div className="bg-purple-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Total {getPeriodDisplayName(selectedPeriod)}</p>
              <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                {total} signups
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400">New users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Day of Week Breakdown */}
      <div className="mt-6">
        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Signups by Day of Week</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {dayNames.map((dayName, index) => {
            const signups = dayTotals[index];
            const percentage = maxSignups > 0 ? (signups / maxSignups) * 100 : 0;
            const isWeekend = index === 0 || index === 6;
            
            return (
              <div key={dayName} className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {dayName.slice(0, 3)}
                </div>
                <div className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {signups}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      isWeekend 
                        ? 'bg-purple-500 dark:bg-purple-400' 
                        : 'bg-blue-500 dark:bg-blue-400'
                    }`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {signupData.length > 0 ? (signups / Math.max(signupData.length / 7, 1)).toFixed(1) : '0.0'} avg
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Day of Week Summary */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-transparent backdrop-blur-md rounded-lg p-3 border border-blue-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Best Weekday</span>
            </div>
            <div className="mt-1">
              <div className="text-lg font-bold text-blue-900 dark:text-blue-100">{bestWeekday}</div>
              <div className="text-xs text-blue-600 dark:text-blue-400">{bestWeekdayCount} total signups</div>
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-transparent backdrop-blur-md rounded-lg p-3 border border-purple-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Weekend Performance</span>
            </div>
            <div className="mt-1">
              <div className="text-lg font-bold text-purple-900 dark:text-purple-100">{weekendTotal}</div>
              <div className="text-xs text-purple-600 dark:text-purple-400">{weekendPercentage}% of total</div>
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-transparent backdrop-blur-md rounded-lg p-3 border border-green-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Most Consistent</span>
            </div>
            <div className="mt-1">
              <div className="text-lg font-bold text-green-900 dark:text-green-100">{mostConsistent.slice(0, 3)}</div>
              <div className="text-xs text-green-600 dark:text-green-400">{consistentCount} signups</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 p-4 bg-gray-50 dark:bg-transparent backdrop-blur-md rounded-lg">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong>💡 Pro tip:</strong> Look for patterns in your peak signup days. Are they weekdays or weekends? 
          Do they correlate with your marketing activities, social media posts, or external events? 
          Use these insights to optimize your content calendar and marketing timing.
        </p>
      </div>
    </div>
  );
};

export default SignupTrendsChart;


